/**
 * Weight is free text on the registration form (qid 3) and only 69.8% of the 199 live values are a
 * clean number. The rest are prose: "19kg", "Approx 16.5 kg", "7,5", "11.85 at last weigh in".
 *
 * The prototype rendered `f.weight + ' kg'`, which was correct for its one sample value and turns
 * into "19kg kg" the moment a real record loads. This module is the fix: parse what can be parsed,
 * and be honest about the rest rather than concatenating a unit onto arbitrary prose.
 *
 * Deliberately NOT a validator that rejects — the value is the client's own answer and the portal
 * is read-only in v1. The job here is to display it without lying.
 */
const HEDGE = /\b(approx|approximately|about|around|circa|ca|roughly|est|estimated|ish)\b|\?|~/i;
/**
 * Read a weight in kg out of free text.
 *
 * Handles: "9.6", "19kg", "16.5 kg", "7,5" (European decimal comma), "Approx 16.5 kg",
 * "11.85 at last weigh in". Refuses ranges ("12-14kg") and multi-number prose, because picking one
 * number out of two is a guess, and a guess here silently drives a wrong portion or dosage.
 */
export function parseWeight(raw) {
    const text = (raw ?? '').trim();
    if (!text) {
        return { raw: '', kg: null, approximate: false, display: '—' };
    }
    const approximate = HEDGE.test(text);
    // Normalise a decimal comma, but only when it sits between digits ("7,5" -> "7.5").
    // A comma used as a separator ("12, 14") must NOT become a decimal point.
    const normalised = text.replace(/(\d),(\d)/g, '$1.$2');
    // A range is not a weight. Bail before the number scan so "12-14kg" does not become 12.
    if (/\d\s*(?:-|–|—|to)\s*\d/i.test(normalised)) {
        return { raw: text, kg: null, approximate, display: text };
    }
    const numbers = normalised.match(/\d+(?:\.\d+)?/g);
    if (!numbers || numbers.length !== 1) {
        // Zero numbers ("Tbc"), or several ("11.85 at last weigh in 2024") — do not guess.
        return { raw: text, kg: null, approximate, display: text };
    }
    const first = numbers[0];
    if (first === undefined) {
        return { raw: text, kg: null, approximate, display: text };
    }
    const kg = Number(first);
    if (!Number.isFinite(kg) || kg <= 0 || kg > 120) {
        // 120 kg is comfortably above the largest breed; beyond it the number is a typo or a
        // different unit, and rendering it as kg would be worse than showing the raw text.
        return { raw: text, kg: null, approximate, display: text };
    }
    const shown = `${kg} kg`;
    return { raw: text, kg, approximate, display: approximate ? `approx ${shown}` : shown };
}
//# sourceMappingURL=weight.js.map